import { assert } from '@fe/core';
import { MapMouseEvent } from 'maplibre-gl';
import { GDFPalletCode } from '../../gdf/type';
import { WeatherPickingInfoLayer } from '../../manager/types';
import palettes, { getPalette, Palette } from '../../palettes/palettes';
import products from '../../products/products';
import { WeatherSettingsChangeSource } from '../../settings/settings';
import { generateRampFromPalette } from '../../utils/ramp';
import { isIcingHotpoly, isTurbulenceHotpoly } from '../../utils/utils';
import { HotpolyMesh, MessageType } from '../../worker/actor/actor-messages';
import { SolidPolygonLayer } from '../custom/polygon/polygon-layer';
import { WeatherLayerBase } from '../weather-layer-base';

export class CustomPolygonLayer extends WeatherLayerBase {
    private layer: SolidPolygonLayer | null = null;
    private data: HotpolyMesh[] | null = null;
    private palette: Palette | null = null;

    protected onAdd(): void {
        this.layer = new SolidPolygonLayer(this.baseId);
        this.map.addLayer(this.layer, this.slotTopLayerId);
        this.loadSettings();
    }

    protected onRemove(): void {
        assert(this.layer);
        this.map.removeLayer(this.layer!.id);
    }

    protected onDisplayChanged(): void {
        this.map.setLayoutProperty(this.baseId, 'visibility', this.shouldDisplay() ? 'visible' : 'none');
    }

    protected onSettingsUpdated(changeSource: WeatherSettingsChangeSource): void {
        if (
            changeSource === WeatherSettingsChangeSource.icing ||
            changeSource === WeatherSettingsChangeSource.turbulenceEdr ||
            changeSource === WeatherSettingsChangeSource.contrails ||
            changeSource === WeatherSettingsChangeSource.enhancedThunderStormMinLevel
        ) {
            this.loadAllFrames().then(() => {
                if (this.visibleFrame !== null) {
                    this.loadFrame(this.visibleFrame);
                }
            });
        } else if (changeSource === WeatherSettingsChangeSource.radarSatellite) {
            if (this.palette?.id === GDFPalletCode.nexrad48) {
                if (this.layer) {
                    this.layer.opacity = this.settings.radar.enableRadarTransparency ? this.settings.radar.radarTransparency / 100 : 1.0;
                }
            }
        } else if (
            changeSource === WeatherSettingsChangeSource.polygonTransparency ||
            changeSource === WeatherSettingsChangeSource.turbulenceTransparency ||
            changeSource === WeatherSettingsChangeSource.icingTransparency
        ) {
            this.loadSettings();
        }
    }

    protected loadFrame(frameId: number): Promise<void> {
        assert(this.layer);
        assert(this.data);

        // Take first visible frame in the visible frames array, do we need to support multiple frames shown at the same time?
        const data = this.data[frameId];
        if (data) {
            this.palette = getPalette(data.palette, this.settings);
            if (this.palette) {
                this.layer.setData(data);

                if (this.palette.id === GDFPalletCode.nexrad48) {
                    this.layer.opacity = this.settings.radar.enableRadarTransparency ? this.settings.radar.radarTransparency / 100 : 1.0;
                }
            }
        }
        return Promise.resolve();
    }

    protected async loadProduct(): Promise<void> {
        await this.loadAllFrames();
    }

    private loadSettings() {
        if (!this.layer) {
            return;
        }
        if (isIcingHotpoly(this.productId)) {
            this.layer.opacity =
                this.settings.misc.enablePolygonTransparency && this.settings.turbulenceIcing.icingApplyTransparency
                    ? this.settings.misc.polygonTransparency / 100
                    : 1.0;
        } else if (isTurbulenceHotpoly(this.productId)) {
            this.layer.opacity =
                this.settings.misc.enablePolygonTransparency && this.settings.turbulenceIcing.turbulenceApplyTransparency
                    ? this.settings.misc.polygonTransparency / 100
                    : 1.0;
        }
    }

    private async loadAllFrames() {
        // Load all frames for quicker animations
        assert(this.info);
        this.data =
            (await this.dispatcher.dispatch({
                type: MessageType.getHotpolyMesh,
                data: {
                    id: this.productId,
                    frames: this.info.frames.map((_, i) => i),
                    workspaceId: this.workspaceId
                }
            })) ?? [];

        const palette = palettes[this.info!.frames[0].header.productPallet];
        const ramp = generateRampFromPalette(palette);
        assert(this.layer);
        this.layer.setRamp(ramp);
    }

    async pickObjects(event: MapMouseEvent): Promise<WeatherPickingInfoLayer | null> {
        const records = await this.dispatcher.dispatch({
            type: MessageType.getTooltipDataHotpoly,
            data: {
                id: this.productId,
                frame: this.visibleFrame!,
                position: event.lngLat.toArray(),
                workspaceId: this.workspaceId
            }
        });

        if (!records || records.length === 0) {
            return null;
        }

        return {
            product: products[this.productId],
            palette: this.palette!.id,
            objects: records
        };
    }
}
